// First-time intake flow — replaces the F1 onboarding stub.
//
// Shown when a logged-in ROLE_PATIENT user has zero profiles. Walks them
// through three small steps:
//   1. Who is this profile for? (SELF / family member)
//   2. Basic details (display name, dob, gender)
//   3. Allergies + emergency contact (optional)
// Then creates the profile via PatientContext.addProfile, which auto-selects
// the new profile and surfaces the rest of the portal.
//
// Per PLAN §6.A — patient empowerment from minute one. We deliberately keep
// this short; the goal is "give us enough to render Today usefully" not
// "capture the full medical history."

import React, { useState } from 'react'
import styled, { ThemeProvider } from 'styled-components'
import patientTheme from '../../theme/patientTheme'
import { usePatient } from '../../context/PatientContext'

const Wrap = styled.div`
  min-height: 100vh;
  background: ${(p) => p.theme?.color?.bg || '#FAF7F2'};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`

const Sheet = styled.div`
  width: 100%;
  max-width: 540px;
  background: ${(p) => p.theme?.color?.bgRaised || '#fff'};
  border-radius: 24px;
  padding: 40px 32px;
  box-shadow: ${(p) => p.theme?.shadow?.raised || '0 4px 16px rgba(31,41,55,0.08)'};
  border: 1px solid ${(p) => p.theme?.color?.border || '#E6E0D6'};
`

const Title = styled.h1`
  font-size: 26px;
  font-weight: 600;
  margin: 0 0 8px;
  color: ${(p) => p.theme?.color?.ink || '#1F2937'};
`

const Sub = styled.p`
  margin: 0 0 24px;
  color: ${(p) => p.theme?.color?.inkMuted || '#6B7280'};
  font-size: 15px;
  line-height: 1.5;
`

const Steps = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 28px;
`

const Dot = styled.span`
  flex: 1;
  height: 4px;
  border-radius: 999px;
  background: ${(p) =>
    p.$active
      ? p.theme?.color?.primary || '#1F3A5F'
      : p.theme?.color?.border || '#E6E0D6'};
`

const Field = styled.div`
  margin-bottom: 14px;
`

const Label = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${(p) => p.theme?.color?.inkMuted || '#6B7280'};
  margin-bottom: 4px;
`

const Input = styled.input`
  width: 100%;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid ${(p) => p.theme?.color?.border || '#E6E0D6'};
  font-family: inherit;
  font-size: 15px;
  &:focus {
    outline: 2px solid ${(p) => p.theme?.color?.primary || '#1F3A5F'};
    outline-offset: 1px;
  }
`

const Select = styled.select`
  width: 100%;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid ${(p) => p.theme?.color?.border || '#E6E0D6'};
  font-family: inherit;
  font-size: 15px;
  background: ${(p) => p.theme?.color?.bgRaised || '#fff'};
`

const RelationGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
`

const RelationCard = styled.button`
  appearance: none;
  border: 2px solid
    ${(p) => (p.$on ? p.theme?.color?.primary || '#1F3A5F' : p.theme?.color?.border || '#E6E0D6')};
  background: ${(p) =>
    p.$on ? p.theme?.color?.primarySoft || '#E8EEF6' : p.theme?.color?.bgRaised || '#fff'};
  border-radius: 14px;
  padding: 16px;
  text-align: left;
  cursor: pointer;
  font-family: inherit;
  font-weight: 600;
  font-size: 15px;
  color: ${(p) => p.theme?.color?.ink || '#1F2937'};
  transition: all 200ms ease-out;
  display: flex;
  align-items: center;
  gap: 12px;
`

const Buttons = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 24px;
  justify-content: space-between;
`

const PrimaryButton = styled.button`
  appearance: none;
  border: none;
  background: ${(p) => p.theme?.color?.primary || '#1F3A5F'};
  color: white;
  font-family: inherit;
  font-size: 15px;
  font-weight: 600;
  padding: 12px 24px;
  border-radius: 999px;
  cursor: pointer;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &:hover:not(:disabled) { background: ${(p) => p.theme?.color?.primaryHover || '#15294A'}; }
`

const GhostButton = styled.button`
  appearance: none;
  border: 1px solid ${(p) => p.theme?.color?.border || '#E6E0D6'};
  background: transparent;
  color: ${(p) => p.theme?.color?.primary || '#1F3A5F'};
  font-family: inherit;
  font-size: 15px;
  font-weight: 600;
  padding: 12px 22px;
  border-radius: 999px;
  cursor: pointer;
  &:hover { background: ${(p) => p.theme?.color?.primarySoft || '#E8EEF6'}; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`

const RELATIONS = [
  { id: 'SELF', emoji: '🙂', label: 'Just me' },
  { id: 'CHILD', emoji: '🧒', label: 'My child' },
  { id: 'PARENT', emoji: '👴', label: 'My parent' },
  { id: 'SPOUSE', emoji: '💞', label: 'My partner' },
  { id: 'OTHER', emoji: '👥', label: 'Someone else' },
]

const AVATAR_COLORS = ['#1F3A5F', '#6B9080', '#B97A2A', '#3F7D58', '#5B5191', '#A56B2C']

function deriveInitials(displayName) {
  if (!displayName) return '??'
  const parts = displayName.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '??'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[1][0]).toUpperCase()
}

export default function PatientIntakeFlow() {
  const { addProfile } = usePatient()
  const [step, setStep] = useState(1)
  const [relation, setRelation] = useState(null)
  const [displayName, setDisplayName] = useState('')
  const [dob, setDob] = useState('')
  const [gender, setGender] = useState('')
  const [allergies, setAllergies] = useState('')
  const [emergencyName, setEmergencyName] = useState('')
  const [emergencyPhone, setEmergencyPhone] = useState('')
  const [emergencyRelation, setEmergencyRelation] = useState('SPOUSE')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  function nextEnabled() {
    if (step === 1) return !!relation
    if (step === 2) return displayName.trim().length > 0
    if (step === 3) return true
    return false
  }

  async function submit() {
    setSubmitting(true)
    setError(null)
    try {
      await addProfile({
        relation,
        displayName: displayName.trim(),
        dob: dob || null,
        gender: gender || null,
        avatarColor: AVATAR_COLORS[Math.floor(Math.random() * AVATAR_COLORS.length)],
        avatarInitials: deriveInitials(displayName),
        allergies: allergies
          .split(/[,\n]+/)
          .map((s) => s.trim())
          .filter(Boolean),
        emergencyContact:
          emergencyName.trim() || emergencyPhone.trim()
            ? {
                name: emergencyName.trim() || null,
                phone: emergencyPhone.trim() || null,
                relation: emergencyRelation,
              }
            : null,
      })
      // PatientContext auto-selects the new profile; the layout re-renders
      // into the full portal automatically.
    } catch (e) {
      setError(e?.message || 'Could not create profile. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <ThemeProvider theme={patientTheme}>
      <Wrap>
        <Sheet>
          <Steps aria-label={`Step ${step} of 3`}>
            {[1, 2, 3].map((n) => (
              <Dot key={n} $active={n <= step} />
            ))}
          </Steps>

          {step === 1 && (
            <>
              <Title>Welcome. Who is this profile for?</Title>
              <Sub>
                You can add more profiles later from the avatar in the top bar — one for yourself
                across each clinic, plus family members.
              </Sub>
              <RelationGrid>
                {RELATIONS.map((r) => (
                  <RelationCard
                    key={r.id}
                    $on={relation === r.id}
                    onClick={() => setRelation(r.id)}
                  >
                    <span style={{ fontSize: 24 }}>{r.emoji}</span>
                    {r.label}
                  </RelationCard>
                ))}
              </RelationGrid>
            </>
          )}

          {step === 2 && (
            <>
              <Title>Basic details</Title>
              <Sub>
                Your practitioner sees these. We&apos;ll use them to scope your records and
                medicines correctly.
              </Sub>
              <Field>
                <Label>{relation === 'SELF' ? 'Your name' : 'Their name'}</Label>
                <Input
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Display name"
                  autoFocus
                />
              </Field>
              <Field>
                <Label>Date of birth (optional)</Label>
                <Input
                  type="date"
                  value={dob}
                  onChange={(e) => setDob(e.target.value)}
                />
              </Field>
              <Field>
                <Label>Gender (optional)</Label>
                <Select value={gender} onChange={(e) => setGender(e.target.value)}>
                  <option value="">Prefer not to say</option>
                  <option value="F">Female</option>
                  <option value="M">Male</option>
                  <option value="OTHER">Other</option>
                </Select>
              </Field>
            </>
          )}

          {step === 3 && (
            <>
              <Title>Anything urgent we should know?</Title>
              <Sub>
                Both of these are optional — you can fill them in later from Profile. Practitioners
                see allergies before any new prescription.
              </Sub>
              <Field>
                <Label>Allergies (optional)</Label>
                <Input
                  value={allergies}
                  onChange={(e) => setAllergies(e.target.value)}
                  placeholder="e.g. penicillin, peanuts"
                />
              </Field>
              <Field>
                <Label>Emergency contact name (optional)</Label>
                <Input
                  value={emergencyName}
                  onChange={(e) => setEmergencyName(e.target.value)}
                  placeholder="Who should we call?"
                />
              </Field>
              <Field>
                <Label>Phone</Label>
                <Input
                  value={emergencyPhone}
                  onChange={(e) => setEmergencyPhone(e.target.value)}
                  placeholder="+91-9..."
                />
              </Field>
              <Field>
                <Label>Relation</Label>
                <Select
                  value={emergencyRelation}
                  onChange={(e) => setEmergencyRelation(e.target.value)}
                >
                  <option value="SPOUSE">Spouse / partner</option>
                  <option value="PARENT">Parent</option>
                  <option value="CHILD">Child</option>
                  <option value="FRIEND">Friend</option>
                  <option value="OTHER">Other</option>
                </Select>
              </Field>
              {error && (
                <p
                  style={{
                    color: '#C24D49',
                    fontSize: 14,
                    margin: 0,
                  }}
                >
                  {error}
                </p>
              )}
            </>
          )}

          <Buttons>
            {step > 1 ? (
              <GhostButton onClick={() => setStep(step - 1)} disabled={submitting}>
                Back
              </GhostButton>
            ) : (
              <span />
            )}
            {step < 3 ? (
              <PrimaryButton
                onClick={() => setStep(step + 1)}
                disabled={!nextEnabled()}
              >
                Continue
              </PrimaryButton>
            ) : (
              <PrimaryButton onClick={submit} disabled={submitting || !displayName.trim()}>
                {submitting ? 'Setting up…' : 'Finish'}
              </PrimaryButton>
            )}
          </Buttons>
        </Sheet>
      </Wrap>
    </ThemeProvider>
  )
}
